import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, EnvelopeSimple, Phone, MapPin, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { SectionHero, SectionHeading } from '../components/SiteBlocks';
import { Button } from '../components/ui/button';
import { aiAPI } from '../lib/api';

const OFFICES = [
  { city: 'Bengaluru (HQ)', address: 'Indiranagar, Bengaluru 560038, Karnataka, India', tz: 'IST (UTC+5:30)' },
  { city: 'Hyderabad', address: 'HITEC City, Hyderabad 500081, Telangana, India', tz: 'IST (UTC+5:30)' },
  { city: 'New York', address: 'Manhattan, NY 10001, USA', tz: 'EST (UTC-5)' },
  { city: 'London', address: 'Shoreditch, EC2A, UK', tz: 'GMT (UTC+0)' },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await aiAPI.submitLead({
        name: form.name,
        email: form.email,
        company: form.company,
        country: 'Unknown',
        description: form.description,
        source: 'website',
      });
      setSent(true);
      toast.success("Thanks! We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please email chandra@xceedagents.ai directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO path="/contact" title="Contact Us" description="Talk to XceedAgents. Send us a message, get a free AI proposal, or schedule a call with our solutions team. India HQ + offices in New York and London." />
      <Navbar />

      <SectionHero eyebrow="Contact" title="Let's talk about your project." subtitle="Tell us what you're building. We'll get back within 24 hours with next steps. Or use our AI to generate a free proposal in 60 seconds." />

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
          {/* FORM */}
          <div>
            <SectionHeading eyebrow="Send a message" title="We respond within 24 hours" />
            {sent ? (
              <div className="mt-8 p-7 rounded-2xl border-2" style={{ borderColor: 'var(--primary-accent)', background: 'rgba(234,88,12,0.05)' }} data-testid="contact-sent">
                <CheckCircle weight="fill" size={32} style={{ color: 'var(--primary-accent)' }} className="mb-3" />
                <h3 className="font-heading text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Message received!</h3>
                <p style={{ color: 'var(--text-secondary)' }}>We'll respond within 24 hours. Meanwhile, try our AI proposal generator for an instant scope and cost estimate.</p>
                <Link to="/start-project" className="mt-5 inline-flex items-center gap-2 font-semibold" style={{ color: 'var(--primary-accent)' }}>
                  Try the AI Proposal Generator <ArrowRight size={16} weight="bold" />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input type="text" placeholder="Your name *" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-lg border outline-none focus:border-orange-500" style={{ borderColor: 'var(--border-strong)', background: 'var(--input-bg)', color: 'var(--text-primary)' }} data-testid="contact-name" />
                  <input type="email" placeholder="Email *" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-lg border outline-none focus:border-orange-500" style={{ borderColor: 'var(--border-strong)', background: 'var(--input-bg)', color: 'var(--text-primary)' }} data-testid="contact-email" />
                </div>
                <input type="text" placeholder="Company" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="w-full px-4 py-3 rounded-lg border outline-none focus:border-orange-500" style={{ borderColor: 'var(--border-strong)', background: 'var(--input-bg)', color: 'var(--text-primary)' }} data-testid="contact-company" />
                <textarea placeholder="Tell us about your project *" rows={5} required value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 rounded-lg border outline-none focus:border-orange-500 resize-none" style={{ borderColor: 'var(--border-strong)', background: 'var(--input-bg)', color: 'var(--text-primary)' }} data-testid="contact-description" />
                <Button type="submit" disabled={submitting} className="w-full h-12 font-semibold gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="contact-submit">
                  {submitting ? 'Sending…' : <>Send Message <ArrowRight size={16} weight="bold" /></>}
                </Button>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>By submitting, you agree to our privacy policy. We never share your data.</p>
              </form>
            )}
          </div>

          {/* CONTACT INFO + OFFICES */}
          <div>
            <SectionHeading eyebrow="Reach us directly" title="Multiple ways to connect" />
            <div className="mt-8 space-y-4">
              <a href="mailto:chandra@xceedagents.ai" className="flex items-center gap-3 p-4 rounded-lg border bg-white hover-lift" style={{ borderColor: 'var(--border)' }} data-testid="contact-email-card">
                <EnvelopeSimple size={22} weight="duotone" style={{ color: 'var(--primary-accent)' }} />
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'var(--text-muted)' }}>Email</div>
                  <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>chandra@xceedagents.ai</div>
                </div>
              </a>
              <a href="tel:+919910059861" className="flex items-center gap-3 p-4 rounded-lg border bg-white hover-lift" style={{ borderColor: 'var(--border)' }} data-testid="contact-phone-card">
                <Phone size={22} weight="duotone" style={{ color: 'var(--primary-accent)' }} />
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'var(--text-muted)' }}>Mobile / WhatsApp</div>
                  <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>+91 99100 59861</div>
                </div>
              </a>
            </div>

            <div className="mt-10">
              <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: 'var(--primary-accent)' }}>Our Offices</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {OFFICES.map((o, i) => (
                  <div key={i} className="p-5 rounded-lg border bg-white" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-start gap-2.5">
                      <MapPin size={18} weight="duotone" style={{ color: 'var(--primary-accent)' }} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-heading font-bold text-base" style={{ color: 'var(--text-primary)' }}>{o.city}</div>
                        <div className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{o.address}</div>
                        <div className="text-xs mt-1 font-mono" style={{ color: 'var(--text-muted)' }}>{o.tz}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
